import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { isFeatureEnabled } from 'owa-feature-flags';
import CalendarEmpty from 'owa-fluent-icons-svg/lib/icons/CalendarEmptyRegular';
import ContactCard from 'owa-fluent-icons-svg/lib/icons/ContactCardRegular';
import Note from 'owa-fluent-icons-svg/lib/icons/NoteRegular';
import PeopleCommunity from 'owa-fluent-icons-svg/lib/icons/PeopleCommunityRegular';
import PersonBoardRegular from 'owa-fluent-icons-svg/lib/icons/PersonBoardRegular';
import TasksApp from 'owa-fluent-icons-svg/lib/icons/TasksAppRegular';
import { getUnifiedGroupsSettingsStore } from 'owa-groups-shared-store/lib/UnifiedGroupsSettingsStore';
import loc from 'owa-localize';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import { getMailMenuItemShouldShowMap } from 'owa-mail-filterable-menu-behavior';
import { MenuItemType } from 'owa-filterable-menu';
import { ControlInfo, getControlInfo } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import { isConsumer } from 'owa-session-store';
import { onNewMessage, onNewNoteClicked, onNewYammerPostClicked } from '../../util/commandActions';
import { createMailAppButton } from '../../util/createMailRibbonButton';
import { openCreateGroup } from '../../util/onGroupsCommandActions';
import { onNewEvent } from '../../util/onNewEvent';
import { isYammerPublisherItemEnabled } from '../../util/isYammerPublisherItemEnabled';

// Placeholder for required onExecute parameter, being used temporarily
// until stubbed out ribbon buttons have been hooked up.
const unhandledOnExecute = () => {};

export default function getNewGroupSections(
    keytipTabId: MailRibbonTabId | undefined
): MenuItemControlProps[] {
    const controls: MenuItemControlProps[] = [];
    const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();

    // Mutually exclusive: New Note and New Mail.
    // We do not show Mail menu item if we're in the Notes folder, as it is disabled and
    // "Note" will be taking that spot. If we're not in Notes folder, we'll show Mail instead.
    if (shouldShowBehaviorMap[MenuItemType.Ribbon_NewNote]()) {
        controls.push(
            createMailAppButton(
                MailRibbonControlId.NewNote,
                loc(Strings.newNote_MenuButton),
                undefined /*tooltip*/,
                false /*isPopout*/,
                onNewNoteClicked,
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                          ]
                        : [getKeytipString(MailRibbonControlId.NewMessageSplitButton)],
                    getKeytipString(MailRibbonControlId.NewNote)
                ),
                Note
            )
        );
    } else {
        const controlInfo: ControlInfo = getControlInfo(MailRibbonControlId.NewMessage);

        controls.push(
            createMailAppButton(
                MailRibbonControlId.NewMessage,
                controlInfo.text,
                controlInfo.tooltip,
                false /*isPopout*/,
                onNewMessage,
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                          ]
                        : [getKeytipString(MailRibbonControlId.NewMessageSplitButton)],
                    getKeytipString(MailRibbonControlId.NewMessage)
                ),
                controlInfo.iconName,
                undefined /*iconColor*/,
                undefined /*styles*/
            )
        );
    }

    controls.push(
        createMailAppButton(
            MailRibbonControlId.NewEvent,
            loc(Strings.newCalEvent_Button),
            undefined /*tooltip*/,
            false /*isPopout*/,
            onNewEvent,
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                      ]
                    : [getKeytipString(MailRibbonControlId.NewMessageSplitButton)],
                getKeytipString(MailRibbonControlId.NewEvent)
            ),
            CalendarEmpty
        )
    );

    const isGroupCreationAllowed: boolean =
        !isConsumer() && (getUnifiedGroupsSettingsStore().groupCreationEnabled ?? false);

    if (isGroupCreationAllowed) {
        controls.push(
            createMailAppButton(
                MailRibbonControlId.NewGroup,
                loc(Strings.addGroup_Button),
                undefined /*tooltip*/,
                false /*isPopout*/,
                openCreateGroup,
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                          ]
                        : [getKeytipString(MailRibbonControlId.NewMessageSplitButton)],
                    getKeytipString(MailRibbonControlId.NewGroup)
                ),
                PeopleCommunity
            )
        );
    }

    if (isFeatureEnabled('mon-ribbon-postSdf')) {
        controls.push(
            createMailAppButton(
                MailRibbonControlId.NewContact,
                loc(Strings.newContact_Button),
                undefined /*tooltip*/,
                false /*isPopout*/,
                unhandledOnExecute,
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                          ]
                        : [getKeytipString(MailRibbonControlId.NewMessageSplitButton)],
                    getKeytipString(MailRibbonControlId.NewContact)
                ),
                ContactCard
            )
        );
        controls.push(
            createMailAppButton(
                MailRibbonControlId.NewTask,
                loc(Strings.newTask_Button),
                undefined /*tooltip*/,
                false /*isPopout*/,
                unhandledOnExecute,
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                          ]
                        : [getKeytipString(MailRibbonControlId.NewMessageSplitButton)],
                    getKeytipString(MailRibbonControlId.NewTask)
                ),
                TasksApp
            )
        );
    }

    if (isYammerPublisherItemEnabled()) {
        controls.push(
            createMailAppButton(
                MailRibbonControlId.NewYammerPost,
                loc(Strings.newPost_Button),
                undefined /*tooltip*/,
                false /*isPopout*/,
                onNewYammerPostClicked,
                constructKeytip(
                    keytipTabId
                        ? [
                              getKeytipString(keytipTabId),
                              getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                          ]
                        : [getKeytipString(MailRibbonControlId.NewMessageSplitButton)],
                    getKeytipString(MailRibbonControlId.NewYammerPost)
                ),
                PersonBoardRegular
            )
        );
    }

    return controls;
}
